import { Avatar, Button, Stack, Typography } from '@mui/material';
import { getPaymentAndDeductions } from 'API/paymentAndDeductions';
import { CustomMetaTitle, LinkReturnToMain } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  IPaymentsDeductionsState,
  PaymentsDeductionsReducer,
} from 'reducer/PaymentsAndDeductions/PaymentsDeductionsReducer';
import { PaymentsAndDeductionsHeader } from './PaymentsAndDeductionsHeader';
import { PaymentsDeductionList } from './PaymentsDeductionList';
import { SearchFilter } from './SearchFilter';
import { AdvancedSearchContextProvider } from 'Context/PaymentDeductionContext/AdvancedSearchProvider';
import { isAfter } from 'date-fns';
import { useLoader } from 'Hooks/useLoader';
import { NoPaymentsAndDeductionsFound } from './NoPaymentsAndDeductionsFound';
import { RequestRefund } from 'Images';
import { useNavigate } from 'react-router-dom';

export const maxPaymentsAndDeductionsPerPage = 20;

export const PaymentsAndDeductions = () => {
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const imposterId = useAppSelector(
    ({ imposter: { imposterId } }) => imposterId
  );

  const [paymentsDeductions, dispatchPaymentsDeductions]: [
    IPaymentsDeductionsState,
    Function
  ] = useReducer(PaymentsDeductionsReducer, {
    paymentsDeductions: [],
    filteredPaymentsDeductions: [],
  });

  const [dateMinMaxRange, setdateMinMaxRange] = useState<{
    min: Date;
    max: Date;
  }>({ min: new Date(), max: new Date() });
  const { isLoading, setIsLoading, loader } = useLoader();
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.PaymentsDeductions);

  const initPaymentsDeductions = useCallback(
    async ({
      isFirstLoad = true,
      shouldOpenSearch = false,
    }: {
      isFirstLoad?: boolean;
      shouldOpenSearch?: boolean;
    }) => {
      setIsLoading(true);
      const result = await getPaymentAndDeductions(
        isFirstLoad ? maxPaymentsAndDeductionsPerPage : undefined
      );
      if (result) {
        dispatchPaymentsDeductions({
          type: 'INITIALIZATION',
          payload: { value: result },
        });
        const minMaxDate = {
          min: result[0]?.statusDate
            ? new Date(result[0].statusDate)
            : new Date(),
          max: result[0]?.statusDate
            ? new Date(result[0].statusDate)
            : new Date(),
        };
        for (const { statusDate } of result) {
          if (!statusDate) continue;
          const open = new Date(statusDate);
          if (isAfter(minMaxDate.min, open)) minMaxDate.min = new Date(open);
          if (isAfter(open, minMaxDate.max)) minMaxDate.max = new Date(open);
        }
        setdateMinMaxRange(minMaxDate);
      }
      setIsLoading(false);
      if (shouldOpenSearch) setAdvancedSearchOpen(true);
    },
    [setIsLoading]
  );

  useEffect(() => {
    initPaymentsDeductions({});
  }, [initPaymentsDeductions, imposterId]);

  return (
    <Stack gap={isDesktop ? '54px' : '24px'} marginBottom={'88px'}>
      <CustomMetaTitle title={dictionary.title} />
      <LinkReturnToMain />
      {isLoading ? (
        <Stack direction='column' maxWidth='1000px' margin='0 auto 50px auto'>
          <PaymentsAndDeductionsHeader />
          {loader}
        </Stack>
      ) : paymentsDeductions.paymentsDeductions.length === 0 ? (
        <NoPaymentsAndDeductionsFound />
      ) : (
        <Stack direction='column' maxWidth='1000px'>
          <AdvancedSearchContextProvider>
            <Stack gap={isDesktop ? '48px' : '24px'}>
              <Stack gap={'40px'}>
                <PaymentsAndDeductionsHeader />
                <Button
                  variant='secondary'
                  sx={{
                    flexDirection: 'column',
                    gap: '8px',
                    maxWidth: isDesktop ? '202px' : '102px',
                    p: isDesktop ? '16px' : '12px 9.5px',
                    color: 'black',
                    boxShadow: isDesktop
                      ? '0px 2px 14px 0px #3A6EEC14'
                      : '0px 1px 22px 0px #1552E81F',
                  }}
                  onClick={() => navigate('/reimbursements')}
                >
                  <Avatar
                    alt=''
                    src={RequestRefund}
                    sx={
                      isDesktop
                        ? { width: '48px', height: '48px' }
                        : { width: '32px', height: '32px' }
                    }
                  />
                  <Typography variant={isDesktop ? 'body3' : 'body5'}>
                    {dictionary.reimbursementRequest}
                  </Typography>
                </Button>
                <SearchFilter
                  paymentsDeductions={paymentsDeductions.paymentsDeductions}
                  dispatchPaymentsDeductions={dispatchPaymentsDeductions}
                  dateMinMaxRange={dateMinMaxRange}
                  loadAllPaymentsDeductions={() =>
                    initPaymentsDeductions({
                      isFirstLoad: false,
                      shouldOpenSearch: true,
                    })
                  }
                  paymentsDeductionsCount={
                    paymentsDeductions.paymentsDeductions.length
                  }
                  advancedSearchOpen={advancedSearchOpen}
                  setAdvancedSearchOpen={setAdvancedSearchOpen}
                />
              </Stack>
              <PaymentsDeductionList
                filteredPaymentsDeductions={
                  paymentsDeductions.filteredPaymentsDeductions
                }
                dispatchPaymentsDeductions={dispatchPaymentsDeductions}
                loadAllPaymentsDeductions={() =>
                  initPaymentsDeductions({ isFirstLoad: false })
                }
                paymentsDeductionsCount={
                  paymentsDeductions.paymentsDeductions.length
                }
              />
            </Stack>
          </AdvancedSearchContextProvider>
        </Stack>
      )}
    </Stack>
  );
};
